import { useModifiersStore } from "../store/ModifiersStoreContext";

type DynamicElementState = {
  /** True if the element has any dynamic content (either general or product-specific) */
  isDynamic: boolean;
  /** True if the element is inside a CurrentProductProvider and has product-specific dynamic content */
  isDynamicProduct: boolean;
  /** True if the element has direct children with data-rid attributes */
  hasDataRidChildren: boolean;
};

/**
 * Hook to check if the currently selected element is dynamic and what type of dynamic it is.
 * Returns an object with:
 * - isDynamic: true if element has data-is-dynamic="true" or data-is-dynamic-product="true"
 * - isDynamicProduct: true specifically for elements inside CurrentProductProvider
 * - hasDataRidChildren: true if element has direct children with data-rid attributes
 *
 * Use isDynamic to determine if editing should be disabled.
 * Use isDynamicProduct to show appropriate tooltip messaging.
 * Use hasDataRidChildren to disable TipTap (which would destroy nested elements).
 */
export const useIsSelectedElementDynamic = (): DynamicElementState => {
  return useModifiersStore((state) => {
    const designation = state.selectedElementDesignations?.find(
      (d) => d.type === "default",
    );
    if (!designation || designation.type !== "default") {
      return {
        isDynamic: false,
        isDynamicProduct: false,
        hasDataRidChildren: false,
      };
    }

    const isGeneralDynamic =
      designation.element.props?.["data-is-dynamic"] === "true";
    const isProductDynamic =
      designation.element.props?.["data-is-dynamic-product"] === "true";
    const hasDataRidChildren =
      designation.element.props?.["data-has-rid-children"] === "true";

    return {
      isDynamic: isGeneralDynamic,
      isDynamicProduct: isProductDynamic,
      hasDataRidChildren,
    };
  });
};
